// =============================================
// /src/pages/AvatarFrameGroupEdit.tsx
// 編集ページ（グループIDは変更不可）— v5 + v8 API 対応
// =============================================
// /src/pages/AvatarFrameGroupEdit.tsx
import React, { useEffect, useRef, useState } from 'react';
import GenericTemplate from '../components/templates/GenericTemplate';
import { useParams, useHistory } from 'react-router-dom';
import { AvatarFrameGroupForm } from './AvatarFrameGroupForm';
import {
  getAvatarFrameGroup,
  updateAvatarFrameGroup,
  uploadFrameImage,
  replaceFrameEntry,
  isFrameIdUniqueExcept, // ← 以前提案した関数を使う場合
  // isFrameIdUnique,    // ← こちらを使うならロジック調整してください
} from '../common/firebase/avatarFrames';
import type { AvatarFrameGroup } from '../types/avatarFrame';
import { Box, CircularProgress, Paper, Stack, Typography } from '@mui/material';
import { BackToAvatarFrameList } from '../components/BackToAvatarFrameList';

const AvatarFrameGroupEdit: React.FC = () => {
  const { groupId } = useParams<{ groupId?: string }>();
  const history = useHistory();

  // ★ マウント状態を記録
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const [value, setValue] = useState<AvatarFrameGroup | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // 初期読込（unmount ガード付き）
  useEffect(() => {
    let active = true; // このエフェクト用の個別フラグ
    (async () => {
      if (!groupId) return;
      if (mountedRef.current) {
        setError(null);
        setLoading(true);
      }
      try {
        const g = await getAvatarFrameGroup(groupId);
        if (!active || !mountedRef.current) return;
        if (!g) throw new Error('グループが見つかりません');
        setValue(g);
      } catch (e: any) {
        if (active && mountedRef.current)
          setError(e?.message ?? '読み込みに失敗しました');
      } finally {
        if (active && mountedRef.current) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [groupId]);

  // 画像アップロード（unmount ガード付き）
  const handleUpload = async (idx: number, file: File) => {
    if (!value) return;
    const f = value.frames[idx];
    if (!f.id) return alert('先にフレームIDを入力してください');
    const { uri, v } = await uploadFrameImage(value.id, file);
    if (!mountedRef.current) return;
    setValue({
      ...value,
      frames: replaceFrameEntry(value.frames, f.id, { uri, v }),
    });
  };

  // 保存（unmount ガード付き）
  const handleSubmit = async () => {
    if (!value) return;
    if (mountedRef.current) {
      setSaving(true);
      setError(null);
    }
    try {
      // ① 同一グループ内重複チェック
      const dupSelfIds = Array.from(
        value.frames.reduce<Map<string, number>>((map, f) => {
          if (!f.id) return map;
          map.set(f.id, (map.get(f.id) || 0) + 1);
          return map;
        }, new Map())
      )
        .filter(([, c]) => c > 1)
        .map(([id]) => id);
      if (dupSelfIds.length > 0) {
        throw new Error(
          `同一グループ内で Frame ID が重複しています: ${dupSelfIds.join(', ')}`
        );
      }

      // ② 他グループとの重複チェック（自分は無視）
      for (const f of value.frames) {
        if (!f.id) continue;
        const uniqueElsewhere = await isFrameIdUniqueExcept(f.id, value.id);
        if (!mountedRef.current) return; // 途中で離脱
        if (!uniqueElsewhere)
          throw new Error(`他グループと Frame ID が重複: ${f.id}`);
      }

      const { id, ...patch } = value as any;
      await updateAvatarFrameGroup(value.id, patch);

      // ここで画面遷移 → この後の setSaving はスキップする
      history.push('/AvatarFrameGroupList');
      return; // ← finally を通らないように return
    } catch (e: any) {
      if (mountedRef.current) setError(e?.message ?? '更新に失敗しました');
    } finally {
      if (mountedRef.current) setSaving(false);
    }
  };

  if (loading)
    return (
      <Stack alignItems="center" sx={{ py: 6 }}>
        <CircularProgress />
      </Stack>
    );

  if (!value)
    return <Typography color="error">{error ?? 'データ無し'}</Typography>;

  return (
    <GenericTemplate title="アバターフレームグループ編集">
      <BackToAvatarFrameList mb={2} />
      <Box component={Paper} sx={{ p: 3 }}>
        <Stack spacing={2}>
          {/* <Typography variant="h5">アバターフレームグループ編集</Typography> */}
          {error ? <Typography color="error">{error}</Typography> : null}
          <AvatarFrameGroupForm
            mode="edit"
            value={value}
            onChange={(next) => setValue(next as any)}
            onUpload={handleUpload}
            onSubmit={handleSubmit}
            submitting={saving}
          />
        </Stack>
      </Box>
    </GenericTemplate>
  );
};

export default AvatarFrameGroupEdit;
